/**
 * PermisFouilleForm.jsx — Permis de Fouille (Lafarge)
 * Sections du template : nature des dangers, précautions, protections collectives,
 * consultations obligatoires (Électricité, Services Généraux, Travaux Neufs),
 * consultations facultatives (Sécurité, Environnement), coordinateur fouilles + exploitant.
 */
import { Shovel } from 'lucide-react';

function YesNo({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      {['oui', 'non'].map((v) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          className={`px-2 py-0.5 text-[11px] font-bold uppercase border ${
            value === v ? (v === 'oui' ? 'bg-green-600 text-white border-green-600' : 'bg-stone-700 text-white border-stone-700') : 'bg-white border-stone-300 text-stone-600'
          }`}>{v}</button>
      ))}
    </div>
  );
}

function Radio3({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      {[
        ['non_necessaire', 'Pas nécessaire', 'bg-stone-500'],
        ['fait', 'Fait', 'bg-green-600'],
        ['a_realiser', 'À réaliser', 'bg-amber-600'],
      ].map(([v, l, c]) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          className={`px-2 py-0.5 text-[10px] font-bold uppercase border ${
            value === v ? `${c} text-white border-transparent` : 'bg-white border-stone-300 text-stone-600'
          }`}>{l}</button>
      ))}
    </div>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>{children}</div>
  );
}

function ConsultBlock({ title, prefix, extra, setExtra, accent = 'bg-stone-700' }) {
  return (
    <div>
      <SectionTitle accent={accent}>{title}</SectionTitle>
      <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nom</div>
          <input type="text" value={extra[`${prefix}_nom`] || ''} onChange={(e) => setExtra(`${prefix}_nom`, e.target.value)}
            data-testid={`pf-${prefix}-nom`} className="w-full px-2 py-1 border border-stone-300" />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Date</div>
          <input type="date" value={extra[`${prefix}_date`] || ''} onChange={(e) => setExtra(`${prefix}_date`, e.target.value)}
            data-testid={`pf-${prefix}-date`} className="w-full px-2 py-1 border border-stone-300" />
        </div>
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Observations / Risques</div>
          <input type="text" value={extra[`${prefix}_obs`] || ''} onChange={(e) => setExtra(`${prefix}_obs`, e.target.value)}
            data-testid={`pf-${prefix}-obs`} className="w-full px-2 py-1 border border-stone-300" />
        </div>
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Visa</div>
          <input type="text" value={extra[`${prefix}_visa`] || ''} onChange={(e) => setExtra(`${prefix}_visa`, e.target.value)}
            data-testid={`pf-${prefix}-visa`} className="w-full px-2 py-1 border border-stone-300" placeholder="Signature / tampon" />
        </div>
      </div>
    </div>
  );
}

export default function PermisFouilleForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, compact = false }) {
  return (
    <div className="space-y-4 border-2 border-amber-700 bg-white" data-testid="permis-fouille-form">
      <div className="bg-amber-700 text-white px-4 py-3 flex items-center gap-3">
        <Shovel className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS DE FOUILLE</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">
            Permis de travail et plan de situation à joindre obligatoirement — Renseigner tous les champs
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Plan de Prévention N°</div>
            <input type="text" value={extra.pp_numero || ''} onChange={(e) => setExtra('pp_numero', e.target.value)}
              data-testid="pf-pp-numero" className="w-full px-2 py-1 border border-stone-300" placeholder="Ex. PP-2026-0123" />
          </div>
          {!compact && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Validité</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateDebut || '—'} → {dateFin || '—'}</div>
          </div>
          )}
        </div>

        {/* 1. Nature des dangers */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">1. Nature des dangers dans la zone des travaux</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-1.5 text-xs bg-red-50/30">
            {[
              ['d_cables', 'Câbles souterrains (galeries)'],
              ['d_canalisations', 'Canalisations enterrées'],
              ['d_eau_potable', 'Eau potable'],
              ['d_egouts', 'Égouts'],
              ['d_sous_cave', 'Abattage en sous-cave'],
              ['d_voie_circulation', 'Travaux sur voie de circulation'],
              ['d_sol_nocif', 'Imprégnation du sol de produits nocifs'],
            ].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <span className="flex-1">{l}</span>
                <YesNo value={extra[k]} onChange={(v) => setExtra(k, v)} testid={`pf-${k}`} />
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="text-[11px]">Autres :</span>
              <input type="text" value={extra.d_autres_desc || ''} onChange={(e) => setExtra('d_autres_desc', e.target.value)}
                data-testid="pf-d-autres-desc" className="flex-1 px-2 py-1 text-xs border border-stone-300" placeholder="Décrire" />
              <YesNo value={extra.d_autres} onChange={(v) => setExtra('d_autres', v)} testid="pf-d-autres" />
            </div>
          </div>
        </div>

        {/* 2. Précautions */}
        <div>
          <SectionTitle>2. Précautions</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-1.5 text-xs">
            {[
              ['p_manuelle', 'Exécution manuelle'],
              ['p_finition_manuelle', 'Finition manuelle'],
              ['p_electricien', "Présence d'un électricien"],
            ].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <span className="flex-1">{l}</span>
                <YesNo value={extra[k]} onChange={(v) => setExtra(k, v)} testid={`pf-${k}`} />
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="text-[11px]">Autres :</span>
              <input type="text" value={extra.p_autres_desc || ''} onChange={(e) => setExtra('p_autres_desc', e.target.value)}
                data-testid="pf-p-autres-desc" className="flex-1 px-2 py-1 text-xs border border-stone-300" />
              <YesNo value={extra.p_autres} onChange={(v) => setExtra('p_autres', v)} testid="pf-p-autres" />
            </div>
          </div>
        </div>

        {/* 3. Protections collectives */}
        <div>
          <SectionTitle accent="bg-amber-700">3. Protections collectives (liste non exhaustive)</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-1.5 text-xs bg-amber-50/30">
            {[
              ['pc_balisage', 'Balisage / signalisation'],
              ['pc_parois_verticales', 'Parois verticales blindées / étrésillonnées / étayées'],
              ['pc_parois_inclinees', 'Parois inclinées'],
              ['pc_talutage', 'Talutage'],
              ['pc_protection_bords', 'Protection des bords (chute personne/objet)'],
              ['pc_acces', 'Accès à la fouille'],
              ['pc_circulation', 'Interruption de la circulation des véhicules'],
              ['pc_eboulement', "Interdiction d'accès à la zone d'éboulement (sous-cave)"],
            ].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between gap-2">
                <span className="flex-1">{l}</span>
                <Radio3 value={extra[k]} onChange={(v) => setExtra(k, v)} testid={`pf-${k}`} />
              </div>
            ))}
            <div className="flex items-center gap-2">
              <input type="text" value={extra.pc_autres_desc || ''} onChange={(e) => setExtra('pc_autres_desc', e.target.value)}
                data-testid="pf-pc-autres-desc" className="flex-1 px-2 py-1 text-xs border border-stone-300" placeholder="Autres : décrire" />
              <Radio3 value={extra.pc_autres} onChange={(v) => setExtra('pc_autres', v)} testid="pf-pc-autres" />
            </div>
          </div>
        </div>

        {/* Consultations obligatoires */}
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#DA291C] pt-2 border-t border-stone-300">
          Consultations obligatoires avant travaux
        </div>
        <ConsultBlock title="Service Électricité" prefix="elec" extra={extra} setExtra={setExtra} accent="bg-yellow-600" />
        <ConsultBlock title="Services Généraux" prefix="sg" extra={extra} setExtra={setExtra} accent="bg-stone-700" />
        <ConsultBlock title="Service Travaux Neufs" prefix="tn" extra={extra} setExtra={setExtra} accent="bg-blue-700" />

        {/* Consultations facultatives */}
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-600 pt-2 border-t border-stone-300">
          Consultations facultatives
        </div>
        <ConsultBlock title="Service Sécurité" prefix="secu" extra={extra} setExtra={setExtra} accent="bg-green-700" />
        <ConsultBlock title="Service Environnement" prefix="env" extra={extra} setExtra={setExtra} accent="bg-emerald-700" />

        {/* Coordinateur des fouilles */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Coordinateur des fouilles (Lafarge ou EE)</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nom</div>
              <input type="text" value={extra.coord_nom || ''} onChange={(e) => setExtra('coord_nom', e.target.value)}
                data-testid="pf-coord-nom" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Date</div>
              <input type="date" value={extra.coord_date || ''} onChange={(e) => setExtra('coord_date', e.target.value)}
                data-testid="pf-coord-date" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={!!extra.coord_ee} onChange={(e) => setExtra('coord_ee', e.target.checked)}
                  data-testid="pf-coord-ee" className="w-4 h-4 accent-[#DA291C]" />
                <span className="text-[11px] font-bold">EE</span>
              </label>
              <input type="text" value={extra.coord_visa || ''} onChange={(e) => setExtra('coord_visa', e.target.value)}
                data-testid="pf-coord-visa" className="flex-1 px-2 py-1 border border-stone-300" placeholder="Visa" />
            </div>
          </div>
        </div>

        {/* Exploitant */}
        <div>
          <SectionTitle>Exploitant</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nom</div>
              <input type="text" value={extra.expl_nom || ''} onChange={(e) => setExtra('expl_nom', e.target.value)}
                data-testid="pf-expl-nom" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Date</div>
              <input type="date" value={extra.expl_date || ''} onChange={(e) => setExtra('expl_date', e.target.value)}
                data-testid="pf-expl-date" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Observations / Risques</div>
              <input type="text" value={extra.expl_obs || ''} onChange={(e) => setExtra('expl_obs', e.target.value)}
                data-testid="pf-expl-obs" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Visa</div>
              <input type="text" value={extra.expl_visa || ''} onChange={(e) => setExtra('expl_visa', e.target.value)}
                data-testid="pf-expl-visa" className="w-full px-2 py-1 border border-stone-300" placeholder="Signature / tampon" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
